import Constants from '@/Constants';
import { QuizQuestion } from '@/models/QuizQuestion';
import { ApplicationState } from '@/store';
import { putQuizQuestionReorderRequest } from '@/store/quiz/action';
import React, { Fragment, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import './style.scss';

interface QuestionButtonProps {
    originalQuestionIndex: number;
}

interface Props {
    currentQuestionIndex: number;
    isCurrentQuestionFormValid: boolean;
    onClickQuestion: CallableFunction;
    onClickNewQuestion: CallableFunction;
    isEditing: boolean;
    hasQuestionChanged: boolean;
}

const QuizQuestionsPanel: React.FC<Props> = (props: Props) => {
    const { currentQuestionIndex, isCurrentQuestionFormValid, onClickQuestion, isEditing, onClickNewQuestion, hasQuestionChanged } = props;

    const { strings, quizQuestions } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        quizQuestions: state.quiz.data.quizData.quizQuestions || [],
    }));

    const [draggableRows, setDraggableRows] = useState<[QuizQuestion[]]>([[]]);
    const [isDraggingDisabled, setIsDraggingDisabled] = useState<boolean>(true);

    const dispatch = useDispatch();

    const QUESTIONS_PER_LINE = 8;

    const getOriginalQuestionIndex = (indexOfRow: number, indexOfQuestion: number) => {
        return indexOfQuestion + indexOfRow * QUESTIONS_PER_LINE;
    };

    const getQuestionStatusStyle = (originalQuestionIndex: number, currQuestionInd: number) => {
        if (!quizQuestions[currQuestionInd] && currQuestionInd !== originalQuestionIndex) return 'disabled';

        return currQuestionInd === originalQuestionIndex ? 'active' : 'inactive';
    };

    const getNewQuestionStatusStyle = () => {
        if (!quizQuestions[currentQuestionIndex]) return 'disabled';

        return '';
    };

    const onClickHandler = (e: any, questionIndex: number) => {
        e.preventDefault();
        if (!isCurrentQuestionFormValid) return false;
        onClickQuestion(questionIndex);
    };

    const setupQuestionsRows = () => {
        const draggableRowsAux: [QuizQuestion[]] = [[]];
        const numberOfDraggableRows = quizQuestions.length / QUESTIONS_PER_LINE;
        let endIndex: number = QUESTIONS_PER_LINE;
        let startIndex: number = 0;

        for (let i = 0; i < numberOfDraggableRows; i++) {
            draggableRowsAux[i] = quizQuestions.slice(startIndex, endIndex);
            startIndex = endIndex.valueOf();
            endIndex += QUESTIONS_PER_LINE;
        }

        setDraggableRows(draggableRowsAux);
    };

    const onDragEnd = (result: DropResult) => {
        const { source, destination } = result;

        if (destination && source && destination.index !== source.index) {
            const qSource = quizQuestions[source.index];

            if (qSource) {
                quizQuestions.splice(source.index, 1);
                quizQuestions.splice(destination.index, 0, qSource);
                dispatch(putQuizQuestionReorderRequest(qSource.id, destination.index));

                if (destination.index === currentQuestionIndex) {
                    onClickQuestion(destination.index < source.index ? ++destination.index : --destination.index);
                } else {
                    onClickQuestion(destination.index);
                }
            }
        }
    };

    /**
     * Adiciona flex-wrap: wrap, caso seja a última questão da linha. Somente para questão em draft
     * para linha de baixo caso tenha chegado ao final do container
     */
    const getLastRowStyle = (iRow: number) => {
        return iRow === draggableRows.length - 1 ? 'question-list-last' : '';
    };

    /**
     * Adicional um margin-top, caso seja a primeira questão em draft da segunda linha em diante.
     */
    const getNewQuestionStyle = (origQuestIndex: number) => {
        const questionsRow = origQuestIndex / QUESTIONS_PER_LINE;
        return !quizQuestions[origQuestIndex] && questionsRow > 0 && Number.isInteger(questionsRow) ? 'last' : '';
    };

    useEffect(() => {
        setupQuestionsRows();
    }, [quizQuestions.length]);

    const QuestionButton: React.FC<QuestionButtonProps> = ({ originalQuestionIndex }: QuestionButtonProps) => {
        const style = getQuestionStatusStyle(originalQuestionIndex, currentQuestionIndex);
        setIsDraggingDisabled(currentQuestionIndex === quizQuestions.length);
        return (
            <a
                className={`question ${style} ${getNewQuestionStyle(originalQuestionIndex)} `}
                href="#"
                onClick={(e) => onClickHandler(e, originalQuestionIndex)}
            >
                <i className={`material-icons-outlined align-middle drag-indicator-icon ${style} `}>{Constants.icons.DRAG_INDICATOR}</i>
                <h4 className={`${style}`}>{`${strings.QUESTION} ${originalQuestionIndex + 1}`}</h4>
            </a>
        );
    };

    return (
        <div className="create-quiz-question-buttons">
            <button
                className={` new-question ${getNewQuestionStatusStyle()}`}
                disabled={!hasQuestionChanged || !isEditing}
                onClick={() => onClickNewQuestion()}
            >
                {strings.NEW_QUESTION}
            </button>
            <DragDropContext onDragEnd={onDragEnd}>
                {draggableRows.map((rowOFquestions: QuizQuestion[], indexRow: number) => {
                    return (
                        <Fragment key={_.uniqueId('row_')}>
                            <Droppable direction="horizontal" droppableId={indexRow.toString()}>
                                {(provided) => (
                                    <div
                                        className={`question-list ${getLastRowStyle(indexRow)}`}
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {rowOFquestions.map((item, indexQuestion) => {
                                            const originalQuestionIndex = getOriginalQuestionIndex(indexRow, indexQuestion);
                                            return (
                                                <Draggable
                                                    draggableId={originalQuestionIndex.toString()}
                                                    index={originalQuestionIndex}
                                                    isDragDisabled={isDraggingDisabled}
                                                    key={item.id}
                                                >
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            style={provided.draggableProps.style}
                                                        >
                                                            <QuestionButton originalQuestionIndex={originalQuestionIndex} />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {!isEditing && indexRow === draggableRows.length - 1 && (
                                            <QuestionButton originalQuestionIndex={quizQuestions.length} />
                                        )}
                                        {!isEditing && indexRow !== draggableRows.length - 1 && provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </Fragment>
                    );
                })}
            </DragDropContext>
        </div>
    );
};

export default QuizQuestionsPanel;
